import { Box, MenuItem, TextField, Typography } from '@mui/material'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  LicensingInternationalPayment,
  PaymentMethodKey,
  paymentsApi,
  PaymentStatusKey,
} from '../../api/swagger'
import useLoadingContext from '../../hooks/useLoadingContext'
import { useLoadingIds } from '../../hooks/useLoadingIds'
import ActionButtons from '../Buttons/ActionButtons'
import { ContainedButtonVariant } from '../Buttons/ContainedButton'
import { TextButtonVariant } from '../Buttons/TextButton'
import BasicModal from './BasicModal'
import {
  getLabelForTransactionOption,
  TransactionOption,
} from './TransactionModal'
import { validateAmount } from './utils/validateAmount'
import { amountFromInput } from '../../utils/amountFromInput'
import { getAmountFromLocaleCurrency } from '../../utils/getAmountFromLocalCurrency'
import { useAppSnackbar } from '../../hooks/useAppSnackbar'

interface Fields {
  key: string
  id: string
  name: string
  label: string
  value: string | number
  onChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void
  onBlur?: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void
  fullWidth?: boolean
  multiline?: boolean
  rows?: number
  helperText?: string
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>
  select?: boolean
  disabled?: boolean
  options?: {
    key: string
    value: string
    label: string
    disabled?: boolean
  }[]
}

type IntlAccountingTransactionModalProps = {
  isOpen: boolean
  onClose: () => void
  userKey: number
  actorKey: number
  payment?: LicensingInternationalPayment
  onTransactionComplete?: () => void
}

export type Step = 'selectTransaction' | 'enterPayment' | 'issueRefund'

const IntlAccountingTransactionModal: React.FC<
  IntlAccountingTransactionModalProps
> = ({
  isOpen,
  onClose,
  userKey,
  actorKey,
  payment,
  onTransactionComplete,
}) => {
  const { t } = useTranslation()
  const {
    PaymentModal: {
      addInternationalLicensingPayment:
        addInternationalLicensingPaymentLoadingId,
      updateLicensingInternationalPayment:
        updateLicensingInternationalPaymentLoadingId,
    },
  } = useLoadingIds()

  const { handleError } = useAppSnackbar()

  const transactionOptions = Object.entries(TransactionOption).map(
    ([key, value]) => ({
      key,
      name: getLabelForTransactionOption(value, t),
    })
  )

  const initialPayment = useRef({
    amount: '0.00',
    description: '',
    tax: '0.00',
    orderId: '',
  })

  const [transactionData, setTransactionData] = useState(initialPayment.current)
  const [currentStep, setCurrentStep] = useState<Step>('selectTransaction')
  const [reasonVoidPayment, setReasonVoidPayment] = useState('')

  let isPrimaryButtonDisabled = !validateAmount(transactionData.amount)

  // If we have a transaction with a positive or zero amount: it is a payment, negative: it is a refund, no transaction: require a selection (default '')
  const [transactionType, setTransactionType] = useState('')
  const [isVoidOrRestoreTransaction, setIsVoidOrRestoreTransaction] =
    useState(false)

  const refineTransactionData = (step: Step) => {
    setTransactionData((prevData) => {
      const refinedAmountString = amountFromInput(prevData.amount)
      const refinedTaxString = amountFromInput(prevData.tax)

      const [refinedAmount, refinedTax] =
        step === 'issueRefund'
          ? [`-${refinedAmountString}`, `-${refinedTaxString}`]
          : [refinedAmountString, refinedTaxString]

      return {
        ...prevData,
        amount: refinedAmount,
        tax: refinedTax,
      }
    })
  }

  useEffect(() => {
    if (payment?.paymentKey) {
      setTransactionType(
        getLabelForTransactionOption(TransactionOption.PaymentReceived, t)
      )

      setTransactionData({
        amount: getAmountFromLocaleCurrency(payment.amount.amount),
        description: payment.desc ?? '',
        tax: payment.tax?.amount.toString() ?? '0.00',
        orderId: payment.orderId ?? '',
      })
      setCurrentStep('enterPayment')
    }
  }, [payment, t])

  // Update the `currentStep` based on the dropdown selection (transactionType)
  useEffect(() => {
    if (transactionType === transactionOptions[0].name) {
      setCurrentStep('enterPayment')
    }

    if (transactionType === transactionOptions[1].name) {
      setCurrentStep('issueRefund')
    }
  }, [transactionOptions, transactionType])

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target

    setTransactionData((prevData) => {
      return { ...prevData, [name]: value }
    })
  }

  useEffect(() => {
    refineTransactionData(currentStep)
  }, [currentStep])

  const handleInputBlur = (
    event: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    const { name } = event.target

    if (name === 'amount' || name === 'tax') {
      refineTransactionData(currentStep)
    }
  }

  const voidOrRestorePayment = () => {
    setIsVoidOrRestoreTransaction(true)
  }

  const cancelVoidOrRestoreTransaction = () => {
    setIsVoidOrRestoreTransaction(false)
  }

  /** ******************* Add Transaction ******************* */
  const handleTransaction = async () => {
    if (!actorKey) return
    try {
      await paymentsApi.addInternationalLicensingPayment({
        body: {
          userKey,
          actorKey,
          amount: getAmountFromLocaleCurrency(transactionData.amount),
          desc: transactionData.description,
          orderId: transactionData.orderId,
          tax: getAmountFromLocaleCurrency(transactionData.tax),
        },
      })
      onTransactionComplete?.()
      onClose()
    } catch (e) {
      await handleError(
        e,
        t(
          'IntlAccountingTransactionModal.ValidationMessage.AddTransactionError',
          'Something went wrong while adding the Payment.'
        )
      )
    }
  }

  const handleUpdateTransaction = async () => {
    try {
      if (!payment) {
        /**
         * This should never happen
         * We should always have a payment if we are in the update flow
         * added only to satisfy ts
         */

        return
      }

      const paymentStatusKey = isVoidOrRestoreTransaction
        ? payment.paymentStatusKey === PaymentStatusKey.Paid
          ? PaymentStatusKey.Void
          : PaymentStatusKey.Paid
        : payment.paymentStatusKey

      await paymentsApi.updateLicensingInternationalPayment({
        paymentKey: payment.paymentKey,
        body: {
          amount: {
            amount: getAmountFromLocaleCurrency(transactionData.amount),
            currencyCode: payment.amount.currencyCode,
          },
          tax: {
            amount: getAmountFromLocaleCurrency(transactionData.tax),
            currencyCode: payment.amount.currencyCode,
          },
          desc: isVoidOrRestoreTransaction
            ? reasonVoidPayment
            : transactionData.description,
          orderId: transactionData.orderId,
          paymentKey: payment.paymentKey,
          begunAt: payment?.begunAt ?? new Date(),
          userKey: payment.userKey,
          paymentMethodKey: PaymentMethodKey.Adj,
          paymentStatusKey,
        },
      })
      onTransactionComplete?.()
      onClose()
    } catch (e) {
      await handleError(
        e,
        t(
          'IntlAccountingTransactionModal.ValidationMessage.AddTransactionError',
          'Something went wrong while adding the Payment.'
        )
      )
    }
  }

  const { triggerFetch: addTransaction } = useLoadingContext({
    asyncFunction: handleTransaction,
    loadingId: addInternationalLicensingPaymentLoadingId,
  })

  const { triggerFetch: updateTransaction } = useLoadingContext({
    asyncFunction: handleUpdateTransaction,
    loadingId: updateLicensingInternationalPaymentLoadingId,
  })

  /** ******************* Modal-related Functions ******************* */
  const DialogContent = () => {
    let amountLabel = t(
      'IntlAccountingTransactionModal.Label.PaymentAmount',
      'Payment Amount (excluding tax)'
    )
    let descriptionLabel = t(
      'IntlAccountingTransactionModal.Label.PaymentDescription',
      'Payment Description (optional)'
    )

    if (currentStep === 'issueRefund') {
      amountLabel = t(
        'IntlAccountingTransactionModal.Label.RefundAmount',
        'Refund Amount (excluding tax)'
      )
      descriptionLabel = t(
        'IntlAccountingTransactionModal.Label.RefundDescription',
        'Refund Description (optional)'
      )
    }

    if (isVoidOrRestoreTransaction) {
      const isVoidingPayment =
        payment?.paymentStatusKey === PaymentStatusKey.Paid

      if (isVoidingPayment) {
        return (
          <Box>
            <Box mb={2}>
              <Typography component="p">
                {t(
                  'EditIntlAccountingTransactionModal.Content.VoidPayment',
                  `Voiding this payment will make it no longer be considered paid. To confirm this void you must enter a reason.`
                )}
              </Typography>
            </Box>
            <TextField
              id="description"
              name="reasonVoidPayment"
              inputProps={{ maxLength: 60, minLength: 6 }}
              label={t('IntlAccountingTransactionModal.VoidPayment', 'Reason')}
              multiline
              rows={8}
              variant="outlined"
              value={reasonVoidPayment}
              onChange={(e) => setReasonVoidPayment(e.target.value)}
              fullWidth
            />
          </Box>
        )
      } else {
        return (
          <Box>
            <Typography component="p">
              {t(
                'EditIntlAccountingTransactionModal.Content.RestorePayment',
                `Restoring this payment will make it be considered paid.`
              )}
            </Typography>
          </Box>
        )
      }
    }

    const fields: Fields[] = [
      {
        key: 'transactionTypeField',
        id: 'transactionTypeField',
        name: 'transactionType',
        label: t(
          'IntlAccountingTransactionModal.Label.Transaction',
          'Transaction'
        ),
        value: transactionType,
        onChange: (e) => setTransactionType(e.target.value),
        fullWidth: true,
        select: true,
        options: transactionOptions?.length
          ? transactionOptions.map((option) => ({
              key: option.key.toString(),
              value: option.name,
              label: option.name,
            }))
          : [
              {
                key: 'no-options',
                value: '',
                label: t(
                  'IntlAccountingTransactionModal.MenuItem.NoOptions',
                  'No Options'
                ),
                disabled: true,
              },
            ],
      },
      {
        key: 'amount',
        id: 'amount',
        name: 'amount',
        label: amountLabel,
        value: transactionData.amount,
        onChange: handleInputChange,
        onBlur: handleInputBlur,
        fullWidth: true,
        disabled:
          isVoidOrRestoreTransaction ||
          payment?.paymentStatusKey === PaymentStatusKey.Void,
      },
      {
        key: 'taxAmount',
        id: 'taxAmount',
        name: 'tax',
        label: t(
          'IntlAccountingTransactionModal.Label.TaxAmount',
          'Tax Amount'
        ),
        value: transactionData.tax,
        onChange: handleInputChange,
        onBlur: handleInputBlur,
        fullWidth: true,
        disabled:
          isVoidOrRestoreTransaction ||
          payment?.paymentStatusKey === PaymentStatusKey.Void,
      },
      {
        key: 'description',
        id: 'description',
        name: 'description',
        label: descriptionLabel,
        value: transactionData?.description ?? '',
        onChange: handleInputChange,
        fullWidth: true,
        multiline: true,
        rows: 4,
        helperText: t(
          'IntlAccountingTransactionModal.Description.HelperText.MaxCharacters',
          '60 characters max'
        ),
        inputProps: { maxLength: 60 },
        disabled:
          isVoidOrRestoreTransaction ||
          payment?.paymentStatusKey === PaymentStatusKey.Void,
      },
      {
        key: 'acumaticaOrderNumberField',
        id: 'acumaticaOrderNumberField',
        name: 'orderId',
        label: t(
          'IntlAccountingTransactionModal.Label.AcumaticaOrderNumber',
          'Acumatica Order # (Optional)'
        ),
        value: transactionData.orderId,
        onChange: handleInputChange,
        fullWidth: true,
      },
    ]

    const renderField = (field: Fields) => (
      <Box
        pb={3.5}
        key={field.key}
        width={field.fullWidth ? '100%' : undefined}
      >
        <TextField
          id={field.id}
          name={field.name}
          label={field.label}
          value={field.value}
          onChange={field.onChange}
          variant="filled"
          fullWidth={field.fullWidth}
          multiline={field.multiline}
          onBlur={field.onBlur}
          rows={field.rows}
          helperText={field.helperText}
          inputProps={field.inputProps}
          disabled={field.disabled}
          select={field.select}
        >
          {field.select &&
            field.options?.map((option) => (
              <MenuItem
                key={option.key}
                value={option.value}
                disabled={option.disabled}
              >
                {option.label}
              </MenuItem>
            ))}
        </TextField>
      </Box>
    )

    return (
      <Box>
        {currentStep === 'selectTransaction'
          ? // Render the first field directly for the initial dropdown
            renderField(fields[0])
          : fields.map(renderField)}
      </Box>
    )
  }

  const DialogTitle = () => {
    let title = t(
      'IntlAccountingTransactionModal.Title.AddNewTransaction',
      'Add New Transaction'
    )

    if (!!payment) {
      if (isVoidOrRestoreTransaction) {
        payment.paymentStatusKey === PaymentStatusKey.Void
          ? (title = t(
              'IntlAccountingTransactionModal.Title.RestorePayment',
              'Are you sure you want to restore this transaction?'
            ))
          : (title = t(
              'IntlAccountingTransactionModal.Title.VoidPayment',
              'Are you sure you want to void this transaction?'
            ))
      } else {
        title = t(
          'IntlAccountingTransactionModal.Title.EditPayment',
          'Edit Payment'
        )
      }
    }

    return title
  }

  let primaryButtonLabel: ContainedButtonVariant
  let secondaryButtonLabel: TextButtonVariant
  let tertiaryButtonLabel: TextButtonVariant | undefined
  let secondaryButtonClick: () => void
  let tertiaryButtonClick: (() => void) | undefined

  primaryButtonLabel = payment
    ? ContainedButtonVariant.UpdatePayment
    : ContainedButtonVariant.Create

  secondaryButtonLabel = TextButtonVariant.Cancel
  secondaryButtonClick = onClose
  tertiaryButtonLabel = undefined
  tertiaryButtonClick = undefined

  if (payment?.paymentStatusKey === PaymentStatusKey.Void) {
    secondaryButtonLabel = TextButtonVariant.RestorePayment
    secondaryButtonClick = voidOrRestorePayment
    tertiaryButtonLabel = TextButtonVariant.Cancel
    tertiaryButtonClick = onClose
  } else if (payment?.paymentStatusKey === PaymentStatusKey.Paid) {
    secondaryButtonLabel = TextButtonVariant.VoidPayment
    secondaryButtonClick = voidOrRestorePayment
    tertiaryButtonLabel = TextButtonVariant.Cancel
    tertiaryButtonClick = onClose
  }

  if (isVoidOrRestoreTransaction) {
    primaryButtonLabel = ContainedButtonVariant.Confirm
    secondaryButtonLabel = TextButtonVariant.NoCancel
    secondaryButtonClick = cancelVoidOrRestoreTransaction
    tertiaryButtonLabel = undefined
    tertiaryButtonClick = undefined

    isPrimaryButtonDisabled =
      reasonVoidPayment.length === 0 &&
      payment?.paymentStatusKey === PaymentStatusKey.Paid
  }

  const DialogActions = () => {
    return (
      <ActionButtons
        primaryButtonLabel={primaryButtonLabel}
        disablePrimaryButton={isPrimaryButtonDisabled}
        secondaryClick={secondaryButtonClick}
        secondaryButtonLabel={secondaryButtonLabel}
        tertiaryButtonLabel={tertiaryButtonLabel}
        tertiaryClick={tertiaryButtonClick}
        alwaysStack
        primaryButtonLoadingId={''}
        useBaseButton
      />
    )
  }

  const resetModalState = () => {
    setTransactionData(initialPayment.current)
    setCurrentStep('selectTransaction')
    setTransactionType('')
  }

  return (
    <BasicModal
      isOpen={isOpen}
      handleFormSubmit={!!payment ? updateTransaction : addTransaction}
      maxWidth="xs"
      dialogContent={DialogContent()}
      dialogActions={DialogActions()}
      ariaLabel={DialogTitle()}
      dialogTitle={DialogTitle()}
      afterClose={resetModalState}
      labelledBy="IntlAccountingTransactionModal-form-title"
    />
  )
}

export default IntlAccountingTransactionModal
